import { AnimatePresence, motion } from "framer-motion";
import React, { useContext, useEffect, useState } from "react";
import { Button, Col, Form, Offcanvas, Row, Spinner, Table } from "react-bootstrap";
import 'react-clock/dist/Clock.css';
import {
  FaClipboardList,
  FaClock,
  FaEdit,
  FaFileCsv,
  FaFileExcel,
  FaFilePdf,
  FaHistory,
  FaPaperclip,
  FaPlus,
  FaTimes,
  FaTrash,
  FaUserAlt
} from "react-icons/fa";
import { FaArrowLeft, FaCheckToSlot, FaListUl, FaSpinner, FaUser } from "react-icons/fa6";
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import ConfirmDialog from "../../UIComponents/ConfirmDialog.tsx";
import { useAppContext } from "../../context/AppContext.tsx";
import { AuthContext } from "../../context/AuthContext.js";
import AdUserDD from "../Tools/AdUserDD.tsx";
import TooltipButton from "../Tools/TooltipButton.tsx";
import DistributionLogViewer from "./DistributionLogViewer.tsx";
import { ProgressType, ReportDistributionModel, ReportModel } from "./_core/_model.ts";
import { DeleteReportDistribution, GetReportDistributionList, UpsertReportDistribution } from "./_core/_request.ts";
import AuditList from "../Audit/AuditObject.tsx";
import { AuditObjectTypes } from "../../_core/_model.ts";
import { ThemeContext } from "../../context/ThemeContext.js";

interface Props {
  report: ReportModel,
  show: boolean;
  onClose: () => void;
}

const SetDistributionModal: React.FC<Props> = ({ show, report, onClose }) => {
  const [mode, setMode] = useState<"list" | "form" | "log" | "audit">("list");
  const [distributionList, setDistributionList] = useState<ReportDistributionModel[] | null>(null);
  const [curIndex, setCurIndex] = useState<number | null>(null);
  const { user } = useContext(AuthContext);
  const { allUsers } = useAppContext();

  const [userList, setUserList] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [frequency, setFrequency] = useState("weekly");
  const [format, setFormat] = useState<string>("link");
  const [dayOption, setDayOption] = useState("Monday");
  const [deliveryTime, setDeliveryTime] = useState<any>("09:00");
  const { themeConfig } = useContext(ThemeContext);

  useEffect(() => {
    if (typeof report != 'undefined') {
      GetReportDistributionList({ reportID: report.reportID, reportAutoID: report.reportAutoID }).then((x) => {
        setDistributionList([...x.data])
      })
    }
  }, [report])

  const resetForm = () => {
    setUserList([]);
    setSelectedUser(null);
    setFrequency("weekly");
    setFormat("link");
    setDayOption("Monday");
    setDeliveryTime("09:00");
  };

  const handleAddUser = () => {
    if (selectedUser && !userList.some(u => u === selectedUser)) {
      setUserList([...userList, selectedUser]);
      setSelectedUser(null);
    }
  };

  const handleRemoveUser = (email: string) => {
    setUserList(userList.filter(u => u !== email));
  };

  function toCronString(frequency: string, dayOption: string, time: string): string {
    const [hour, minute] = time.split(":");
    if (frequency === "daily") {
      return `${parseInt(minute)} ${parseInt(hour)} * * *`;
    } else if (frequency === "weekly") {
      const dayMap: Record<string, string> = {
        Monday: "1", Tuesday: "2", Wednesday: "3", Thursday: "4", Friday: "5"
      };
      return `${parseInt(minute)} ${parseInt(hour)} * * ${dayMap[dayOption] ?? "1"}`;
    }
    return "0 9 * * *";
  }

  function fromCronString(cron: string): { frequency: string, dayOption: string, time: string } {
    const parts = cron.trim().split(" ");
    const hr = parts[1]?.padStart(2, "0") || "09";
    const min = parts[0]?.padStart(2, "0") || "00";
    const time = `${hr}:${min}`;

    if (parts.length === 5) {
      const dayOfWeek = parts[4];
      if (dayOfWeek === "*") {
        return { frequency: "daily", dayOption: "", time };
      } else {
        const revDayMap: Record<string, string> = {
          "1": "Monday", "2": "Tuesday", "3": "Wednesday", "4": "Thursday", "5": "Friday"
        };
        return { frequency: "weekly", dayOption: revDayMap[dayOfWeek] ?? "Monday", time };
      }
    }
    return { frequency: "daily", dayOption: "", time };
  }

  const [progress, setProgress] = useState(false);

  const handleSubmit = () => {
    if (distributionList == null)
      return;
    setProgress(true);
    //{ userList, frequency, format, urlFormat, dayOption };
    const newDist: ReportDistributionModel = {
      reportDistributionID: (curIndex !== null && curIndex > -1) ? distributionList[curIndex].reportDistributionID : 0,
      reportID: report.reportID,
      distributionName: dayOption,
      distributionSchedule: toCronString(frequency, dayOption, deliveryTime),
      distributionUsers: userList.join(","),
      isActive: 1,
      nextRunAt: null,
      lastRunAt: null,
      lastStatus: null,
      lastErrorMessage: null,
      createdBy: user.userID,
      createdAt: null,
      format: format,
      progress: ProgressType.Save
    }
    UpsertReportDistribution(newDist).then((x) => {
      let dList = (distributionList == null) ? [] : distributionList;
      newDist.reportDistributionID = x.data;
      if (curIndex !== null) {
        const updatedList = [...dList];
        updatedList[curIndex] = newDist;
        setDistributionList(updatedList);
      } else {
        setDistributionList(prev => prev == null ? [...[newDist]] : [...prev, newDist]);
      }
      resetForm();
      setMode("list");
      setCurIndex(null);
      setProgress(false);
    })
  };

  const handleDelete = (index: number) => {
    if (distributionList != null) {
      let nList = distributionList;;
      nList[index].progress = ProgressType.DeleteStart;
      setDistributionList([...nList]);
      DeleteReportDistribution(nList[index].reportDistributionID, user.userID).then((x) => {
        let nList = distributionList;
        nList.splice(index, 1);
        setDistributionList([...nList]);
      })
    }
  }

  const handleEdit = (index: number) => {
    if (distributionList != null) {
      const dist = distributionList[index];
      const timeParts = dist.distributionSchedule.split(" ");
      if (timeParts.length >= 2) {
        const hr = timeParts[1];
        const min = timeParts[0];
        setDeliveryTime(`${hr.padStart(2, "0")}:${min.padStart(2, "0")}`);
      }
      setUserList(dist.distributionUsers.split(","));
      let obj = fromCronString(dist.distributionSchedule);
      setFrequency(obj.frequency);
      setDayOption(obj.dayOption);
      setDeliveryTime(obj.time);
      // setFormat(dist.format);
      // setUrlFormat(dist.urlFormat);      
      setCurIndex(index);
      setMode("form");
    }
  };

  const usersEmails = (usr: string) => {
    let uArr = usr.split(",");
    return allUsers.filter((u) => uArr.indexOf(u.userID.toString()) > -1).map((u) => u.email).join(",");
  }

  return (
    // <Modal show={show} onHide={onClose} centered backdrop="static" keyboard={false} dialogClassName="dl-modal" contentClassName="border-0">
    <Offcanvas show={show} className="card" onHide={() => onClose()} placement="end" style={{ width: '600px' }}>
      <Offcanvas.Header className="audit-header border-bottom flex-row w-100 pb-0 align-items-center justify-content-between pb-3 w-100">
        <div className="d-flex align-items-center gap-2">
          {mode === "form" && (
            <Button variant="light" size="sm" onClick={() => { resetForm(); setMode("list"); setCurIndex(null); }}>
              <FaArrowLeft />
            </Button>
          )}
          <h5 className="m-0 d-flex align-items-center gap-2">
            <FaClock />
            {mode === "list" ? `Distributions for ${report.reportName}` :
              mode === "log" ? `Distribution Logs for ${report.reportName}` :
                (mode == "audit") ? `Audit Trails for ${report.reportName}` :
                  curIndex != null ?
                    `Edit Distribution: ${report.reportName}` : "Add New Distribution"}
          </h5>
        </div>
        <div className="d-flex flex-row align-items-center gap-2">
          {
            (mode == "list" || (mode == "form" && curIndex != null && curIndex > -1)) &&
            <TooltipButton title="Audit Log for Distributions">
              <Button variant="outline-secondary"
                onClick={() => setMode("audit")}
                style={{ boxShadow: 'none' }}
              >
                <FaClipboardList size={16} />
              </Button>
            </TooltipButton>
          }
          <Button variant="outline-secondary" onClick={onClose}><FaTimes /></Button>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body style={{ padding: "0px", overflowX: "hidden" }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={mode} // Key is essential to trigger animation on switch
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -30, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ marginTop: "16px" }}
          >
            {mode === "list" ? (
              <>
                <div className="px-4">
                  {
                    distributionList == null ?
                      <div className="d-flex flex-column align-items-center text-success mt-4">
                        <AnimatePresence>
                          <motion.div
                            animate={{ rotate: 360 }}
                            style={{
                              display: 'inline-block',
                              width: '40px',
                              height: '40px',
                              marginBottom: '10px'
                            }}
                            transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                          >
                            <FaSpinner className="fa-spin mb-2" size={32} style={{
                              fontSize: '2.5rem',
                              color: '#198754',
                              width: '100%',
                              height: '100%'
                            }} />
                          </motion.div>
                        </AnimatePresence>
                        <span>Loading Distributions...</span>
                      </div> :
                      distributionList.length == 0 ?
                        <AnimatePresence>
                          <motion.div
                            className="d-flex flex-column align-items-center justify-content-center text-muted-theme px-5 w-100"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, ease: "easeOut" }}
                            style={{
                              border: "2px dashed #dee2e6",
                              borderRadius: "10px",
                              backgroundColor: themeConfig.colors.background,
                              marginBottom: "10px"
                            }}
                          >
                            <FaCheckToSlot size={60} color="#adb5bd" className="mb-3" />
                            <h5 className="fw-semibold">No Distribution Found</h5>
                            <p className="text-center" style={{ maxWidth: 400 }}>
                              No distributions added yet for {report.reportName} report.
                            </p>
                          </motion.div>
                        </AnimatePresence> :
                        distributionList.map((d, i) => (
                          <div className="card shadow-sm border-0 mb-3 mx-2 p-3 bg-light rounded-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="d-flex flex-column gap-1">
                                <div className="d-flex align-items-center gap-2 text-muted-theme small">
                                  <FaClock className="text-primary" />
                                  <span>Frequency:</span>
                                  <span className="fw-semibold text-dark">
                                    {fromCronString(d.distributionSchedule).frequency}
                                    {fromCronString(d.distributionSchedule).frequency === "weekly" && (
                                      <> ({fromCronString(d.distributionSchedule).dayOption})</>
                                    )}
                                  </span>
                                </div>

                                <div className="d-flex align-items-center gap-2 text-muted-theme small">
                                  <FaPaperclip className="text-success" />
                                  <span>Delivery:</span>
                                  <span className="fw-semibold text-dark text-capitalize">{d.format || "link"}</span>
                                </div>
                              </div>

                              <div className="d-flex align-items-center gap-2">
                                <TooltipButton title="View Logs">
                                  <Button variant="outline-secondary" size="sm" onClick={() => {
                                    setCurIndex(i);
                                    setMode("log");
                                  }}>
                                    <FaListUl />
                                  </Button>
                                </TooltipButton>
                                <TooltipButton title="Edit">
                                  <Button variant="outline-secondary" size="sm" onClick={() => handleEdit(i)}>
                                    <FaEdit />
                                  </Button>
                                </TooltipButton>
                                <TooltipButton title="Delete">
                                  <Button variant="outline-danger" size="sm" onClick={() => {
                                    let nList = distributionList;
                                    nList[i].progress = ProgressType.DeleteStart;
                                    setDistributionList([...nList]);
                                  }}>
                                    {d.progress === ProgressType.Deleting ? <Spinner animation="border" size="sm" /> : <FaTrash />}
                                    <ConfirmDialog
                                      show={d.progress === ProgressType.DeleteStart}
                                      mode="modal"
                                      title="Report Distribution"
                                      message="Are you sure you want to delete this Distribution?"
                                      onCancel={(e) => {
                                        e.preventDefault(); e.stopPropagation();
                                        distributionList[i].progress = ProgressType.None;
                                        setDistributionList([...distributionList]);
                                      }}
                                      onConfirm={(e) => {
                                        e.preventDefault(); e.stopPropagation();
                                        handleDelete(i);
                                      }}
                                      loading={d.progress === ProgressType.Deleting}
                                    />
                                  </Button>
                                </TooltipButton>
                              </div>
                            </div>

                            <div className="border rounded p-2 bg-theme-card mt-2">
                              <div className="d-flex align-items-center gap-2 mb-2 text-muted-theme small">
                                <span>Recipients</span>
                              </div>
                              {d.distributionUsers.split(",").map((u, index, arr) => (
                                <div
                                  key={index}
                                  className={`py-1 px-2 d-flex align-items-center gap-2 ${index !== arr.length - 1 ? 'border-bottom' : ''}`}
                                >
                                  <FaUser className="text-muted-theme" />
                                  <span className="text-dark">{u}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                        ))
                  }
                </div>
                <div className="d-flex justify-content-end px-4 my-2">
                  <Button variant="primary" onClick={() => { resetForm(); setMode("form"); }}>Add New Distribution</Button>
                </div>
              </>
            ) : (mode == "form") ? (
              <motion.div key={mode} /*...animation props*/>
                <Row className="g-4 px-4 pt-3">
                  {/* LEFT SIDE: Schedule & Format */}
                  <Col md={5}>
                    <div className="border rounded shadow-sm p-3 bg-theme-card">
                      <h6 className="mb-3 d-flex align-items-center gap-2 text-primary">
                        <FaClock /> Delivery Schedule
                      </h6>

                      <Form.Group className="mb-3">
                        <Form.Label className="text-muted-theme small">Time of Delivery</Form.Label>
                        <div>
                          <TimePicker
                            value={deliveryTime}
                            onChange={setDeliveryTime}
                            className="bootstrap-time-picker"
                            disableClock={true} // optional: keep only dropdowns
                            clearIcon={null}
                          />
                        </div>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label className="text-muted-theme small">Frequency</Form.Label>
                        <Form.Select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                        </Form.Select>
                      </Form.Group>

                      {frequency === "weekly" && (
                        <Form.Group className="mb-3">
                          <Form.Label className="text-muted-theme small">Day of Week</Form.Label>
                          <Form.Select value={dayOption} onChange={(e) => setDayOption(e.target.value)}>
                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => (
                              <option key={day} value={day}>{day}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      )}
                    </div>

                    <div className="border rounded shadow-sm p-3 bg-theme-card mt-4">
                      <h6 className="mb-3 d-flex align-items-center gap-2 text-primary">
                        <FaPaperclip /> Delivery Type
                      </h6>

                      {['link', 'attachment'].map(opt => (
                        <Form.Check
                          key={opt}
                          type="radio"
                          name="urlFormat"
                          label={<span className="ms-2">{opt === 'link' ? 'View Link' : 'Email Attachment'}</span>}
                          value={opt}
                          checked={(opt === "link" && format === "link") || (opt !== "link" && format !== "link")}
                          onChange={() => setFormat(opt === 'link' ? 'link' : 'pdf')}
                        />
                      ))}

                      {format !== 'link' && (
                        <div className="mt-3">
                          <Form.Label className="text-muted-theme small">File Format</Form.Label>
                          {[{ id: 'pdf', icon: <FaFilePdf className="text-danger" /> },
                          { id: 'excel', icon: <FaFileExcel className="text-success" /> },
                          { id: 'csv', icon: <FaFileCsv className="text-primary" /> }
                          ].map(({ id, icon }) => (
                            <Form.Check
                              key={id}
                              type="radio"
                              name="format"
                              label={<span className="ms-2 d-flex align-items-center gap-2">{icon} {id.toUpperCase()}</span>}
                              value={id}
                              checked={format === id}
                              onChange={() => setFormat(id)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* RIGHT SIDE: User Selection */}
                  <Col md={7}>
                    <div className="border rounded shadow-sm p-3 bg-theme-card">
                      <h6 className="mb-3 d-flex align-items-center gap-2 text-primary">
                        <FaUserAlt /> Distribution Users
                      </h6>

                      <Form.Group className="mb-3">
                        <div className="d-flex gap-2">
                          <div className="flex-fill">
                            <AdUserDD
                              needLoginUser={true}
                              placeholder="Select a user"
                              avoidUser={allUsers.filter((x) => userList.includes(x.email)).map((x) => x.userID)}
                              initSelect={allUsers.find((u) => u.email == selectedUser)?.userID.toString() ?? ""}
                              updateUser={(u) => u && setSelectedUser(u.userEmail)}
                            />
                            {selectedUser && <div className="text-muted-theme small mt-1">Click '+' to add</div>}
                          </div>
                          <Button variant="success" style={{ height: "32px" }} onClick={handleAddUser} disabled={!selectedUser}><FaPlus /></Button>
                        </div>
                      </Form.Group>

                      {userList.length > 0 && (
                        <div className="table-responsive border rounded mt-3">
                          <Table size="sm" bordered>
                            <thead className="table-light">
                              <tr><th>Name</th><th>Email</th><th></th></tr>
                            </thead>
                            <tbody>
                              {userList.map((u) => (
                                <tr key={u}>
                                  <td>{allUsers.find(x => x.email === u)?.userFirstName}</td>
                                  <td>{u}</td>
                                  <td className="text-center">
                                    <Button variant="outline-danger" size="sm" onClick={() => handleRemoveUser(u)}>
                                      <FaTrash />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </motion.div>

            ) : (mode == "log") ?
              distributionList != null && curIndex != null &&
              <DistributionLogViewer {...{
                report: report,
                reportDistributionID: distributionList[curIndex].reportDistributionID,
                onBack: () => {
                  setCurIndex(null);
                  setMode("list");
                }
              }} /> :
              <div className="d-flex flex-column w-100">
                <div className="px-2">
                  <Button variant="outline-primary" onClick={() => {
                    if (curIndex != null && curIndex > -1)
                      setMode("form")
                    else
                      setMode("list");
                  }}>
                    <FaArrowLeft className="me-2" />
                    {
                      (curIndex != null && curIndex > -1) ?
                        "Back to Edit" : "Back"
                    }
                  </Button>
                </div>
                <AuditList {...{
                  instanceId: (curIndex == null || curIndex == -1 || distributionList == null) ? 0 : distributionList[curIndex].reportDistributionID,
                  objectName: AuditObjectTypes.ReportDistributions, auditObjectName: "", setShowAudit: () => { },
                  showAudit: true, asDrawer: false
                }} />
              </div>

            }
          </motion.div>
        </AnimatePresence>
        <div className="px-4 py-3 border-top bg-theme-card d-flex justify-content-between align-items-center mt-3">
          <div>
            {mode === "form" && (
              <Button variant="secondary" onClick={() => { resetForm(); setCurIndex(null); setMode("list"); }}>
                <FaArrowLeft className="me-2" /> Back
              </Button>
            )}
          </div>
          <div className="d-flex gap-2">
            {mode === "form" && (
              <Button variant="success" disabled={userList.length === 0} onClick={handleSubmit}>
                {progress ? (
                  <span className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    Saving...
                  </span>
                ) : (
                  "Save Distribution"
                )}
              </Button>
            )}
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas >
    // </Modal>
  );
};

export default SetDistributionModal;
